import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Instagram, ArrowRight } from "lucide-react";
import { API, SITE, IMAGES } from "../data/site";
import { useSettings, useContent } from "../hooks/useCms";
import PageHeader from "../components/PageHeader";
import { FadeUp } from "../components/motion";
import CyclingImage from "../components/CyclingImage";
import SEO from "../components/SEO";

const INTERESTS = ["Buying an Available Home", "Build a Custom Home", "General Question"];

const inputCls =
  "w-full bg-transparent border-0 border-b border-[#1A1A1A]/25 focus:border-[#8C6A4F] focus:ring-0 outline-none py-4 text-base placeholder:text-[#A3A09B] transition-colors duration-300 rounded-none";

const Field = ({ label, children, testid }) => (
  <label className="block" data-testid={testid}>
    <span className="eyebrow text-[#5C5A56] block mb-1">{label}</span>
    {children}
  </label>
);

const Contact = () => {
  const [params] = useSearchParams();
  const [sending, setSending] = useState(false);
  const settings = useSettings();
  const c = useContent();
  const phone = settings.phone || SITE.phone;
  const email = settings.email || SITE.email;
  const instagram = settings.instagram || SITE.instagram;
  const instagramHandle = settings.instagram_handle || SITE.instagramHandle;
  const region = settings.region || SITE.region;
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    interest: params.get("interest") || "",
    location: "",
    home_size: "",
    budget: "",
    timeframe: "",
    message: params.get("home") ? `I'm interested in ${params.get("home")}. ` : "",
    home: params.get("home") || "",
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success("Thank you — we'll be in touch soon.");
      setForm({ ...form, name: "", email: "", phone: "", location: "", home_size: "", budget: "", timeframe: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main>
      <SEO />
      <PageHeader
        testid="contact-header"
        eyebrow="Contact On Point Homes"
        lines={[{ text: c("contact_headline1", "Start Your") }, { text: c("contact_headline2", "Project"), italic: true }]}
        description={c("contact_intro", "Tell us about what you're looking for - an available home, a custom build, or a general question. We read every message personally and respond quickly.")}
      />

      <section className="pb-28 md:pb-40">
        <div className="max-w-[1600px] mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <FadeUp className="lg:col-span-7">
            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8" data-testid="contact-form">
              <Field label="Name *" testid="field-name">
                <input required value={form.name} onChange={set("name")} className={inputCls} placeholder="Your full name" data-testid="input-name" />
              </Field>
              <Field label="Email *" testid="field-email">
                <input required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@email.com" data-testid="input-email" />
              </Field>
              <Field label="Phone" testid="field-phone">
                <input value={form.phone} onChange={set("phone")} className={inputCls} placeholder="(208) 555-0000" data-testid="input-phone" />
              </Field>
              <Field label="What are you interested in?" testid="field-interest">
                <select required value={form.interest} onChange={set("interest")} className={`${inputCls} cursor-pointer ${form.interest === "" ? "text-[#A3A09B]" : ""}`} data-testid="input-interest">
                  <option value="" disabled>Select one…</option>
                  {INTERESTS.map((i) => <option key={i}>{i}</option>)}
                </select>
              </Field>
              <Field label="Where are you looking to build?" testid="field-location">
                <input value={form.location} onChange={set("location")} className={inputCls} placeholder="City, area, or 'need help finding land'" data-testid="input-location" />
              </Field>
              <Field label="Estimated Home Size" testid="field-size">
                <input value={form.home_size} onChange={set("home_size")} className={inputCls} placeholder="e.g. 2,500 sq ft" data-testid="input-size" />
              </Field>
              <Field label="Estimated Budget" testid="field-budget">
                <input value={form.budget} onChange={set("budget")} className={inputCls} placeholder="e.g. $650k – $800k" data-testid="input-budget" />
              </Field>
              <Field label="Desired Timeframe" testid="field-timeframe">
                <input value={form.timeframe} onChange={set("timeframe")} className={inputCls} placeholder="e.g. Start this fall" data-testid="input-timeframe" />
              </Field>
              <div className="md:col-span-2">
                <Field label="Tell us about your project" testid="field-message">
                  <textarea rows={5} value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} placeholder="How you live, what you love, what you're dreaming about…" data-testid="input-message" />
                </Field>
              </div>
              <div className="md:col-span-2 pt-4">
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-submit-btn"
                  className="btn-sweep bg-[#1A1A1A] text-[#F7F5F2] font-mono text-[0.7rem] tracking-[0.2em] uppercase font-bold px-10 py-5 inline-flex items-center gap-3 disabled:opacity-50"
                >
                  {sending ? "Sending…" : c("contact_cta_button", "Start Your Project")} <ArrowRight size={14} />
                </button>
              </div>
            </form>
          </FadeUp>

          <FadeUp className="lg:col-span-5" delay={0.15}>
            <div className="lg:sticky lg:top-28 flex flex-col gap-10">
              <div className="img-zoom relative aspect-[4/3]">
                <CyclingImage start={20} interval={5600} alt="Homes built by On Point Homes in Idaho's Treasure Valley" />
              </div>
              <div className="border border-[rgba(26,26,26,0.15)] p-8">
                <p className="eyebrow text-[#8C6A4F] mb-6">Reach Us Directly</p>
                <div className="flex flex-col gap-4 text-sm text-[#5C5A56]">
                  <a href={`tel:${phone}`} data-testid="contact-phone" className="flex items-center gap-3 hover:text-[#1A1A1A] transition-colors duration-300"><Phone size={15} className="text-[#8C6A4F]" /> {phone}</a>
                  <a href={`mailto:${email}`} data-testid="contact-email" className="flex items-center gap-3 hover:text-[#1A1A1A] transition-colors duration-300"><Mail size={15} className="text-[#8C6A4F]" /> {email}</a>
                  <a href={instagram} target="_blank" rel="noreferrer" data-testid="contact-instagram" className="flex items-center gap-3 hover:text-[#1A1A1A] transition-colors duration-300"><Instagram size={15} className="text-[#8C6A4F]" /> {instagramHandle}</a>
                  <span className="flex items-center gap-3"><MapPin size={15} className="text-[#8C6A4F]" /> {region}</span>
                </div>
                <p className="mt-6 pt-6 border-t border-[rgba(26,26,26,0.1)] text-xs text-[#A3A09B] leading-relaxed">
                  Touring an available home? Mention the home's name in your message and we'll set up a private walkthrough.
                </p>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>
    </main>
  );
};

export default Contact;
